import { Link } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, Loader2, Pause, Play, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AdminCoupon,
  type CouponRedemption,
  useAdminCoupon,
  useCouponRedemptions,
  usePatchCoupon,
  useSoftDeleteCoupon,
} from '@/shared/api/admin-coupons';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

/**
 * Drill-down for one coupon.  Surfaces:
 *   • Identity card — code (uppercase), kind, discount, redemption counter
 *     vs cap, validity window, scoped plans / intervals, first-time-only
 *     flag, notes.
 *   • Live edit controls (patch endpoint):
 *       - Pause / resume    (toggle isActive)
 *       - Raise max_redemptions (or set unlimited)
 *       - Extend valid_until (push expiry)
 *       - Edit notes
 *     Every patch carries a reason and lands in the audit log.
 *   • Redemptions table — every recorded coupon use with customer
 *     reference (linked to /users/$id or /teams/$id) and discount applied.
 *
 * Phase 1 caveat: redemption count is incremented at checkout by
 * CouponsService, but checkout itself doesn't fire until Stripe wires.
 * Until then this table is mostly empty for organic coupons; the founder
 * can still admin-create + admin-grant to test the trail.
 */
export function CouponDetailPage({ id }: { id: string }) {
  const { data: coupon, isLoading, error } = useAdminCoupon(id);
  const { data: redemptions } = useCouponRedemptions(id);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="grid place-items-center py-12 text-neutral-500">
          <Loader2 className="size-5 animate-spin" />
        </div>
      </AdminLayout>
    );
  }
  if (error || !coupon) {
    return (
      <AdminLayout>
        <div className="rounded-lg border border-red-900 bg-red-950/30 p-4 text-sm text-red-300">
          Coupon not found or not accessible.
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Link
        to="/coupons"
        className="mb-4 inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-neutral-200"
      >
        <ArrowLeft size={14} /> All coupons
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">
            <code className="rounded bg-neutral-800 px-2 py-1 font-mono uppercase tracking-widest text-orange-400">
              {coupon.code}
            </code>
            <span className="ml-3 align-middle">
              <StatusBadge isActive={coupon.isActive} />
            </span>
          </h1>
          <p className="mt-2 text-sm text-neutral-400">
            {humanKind(coupon)} · created {formatDateTime(coupon.createdAt)}
          </p>
        </div>
        <CouponActions coupon={coupon} />
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <Card title="Discount & scope">
          <DataRow label="Kind" value={coupon.kind} />
          <DataRow label="Discount" value={formatDiscount(coupon)} />
          <DataRow
            label="Applies to plans"
            value={
              coupon.appliesToPlans.length === 0
                ? <span className="text-neutral-500">all plans</span>
                : coupon.appliesToPlans.join(', ')
            }
          />
          <DataRow
            label="Applies to intervals"
            value={
              coupon.appliesToIntervals.length === 0
                ? <span className="text-neutral-500">monthly + annual</span>
                : coupon.appliesToIntervals.join(', ')
            }
          />
          <DataRow
            label="First-time customer only"
            value={coupon.firstTimeCustomerOnly ? 'yes' : 'no'}
          />
        </Card>

        <Card title="Validity & redemptions">
          <DataRow label="Valid from" value={formatDateTime(coupon.validFrom)} />
          <DataRow
            label="Valid until"
            value={coupon.validUntil ? formatDateTime(coupon.validUntil) : <span className="text-neutral-500">no expiry</span>}
          />
          <DataRow
            label="Redemptions"
            value={
              <>
                <span className="font-semibold text-neutral-100">{coupon.redemptionCount}</span>
                {coupon.maxRedemptions !== null ? (
                  <span className="text-neutral-500"> / {coupon.maxRedemptions}</span>
                ) : (
                  <span className="text-neutral-500"> / unlimited</span>
                )}
              </>
            }
          />
          {coupon.maxRedemptions !== null && coupon.redemptionCount >= coupon.maxRedemptions ? (
            <div className="mt-2 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-300 ring-1 ring-amber-500/30">
              Cap reached — coupon will refuse new redemptions until raised or removed.
            </div>
          ) : null}
          {coupon.notes ? (
            <DataRow
              label="Notes"
              value={<span className="italic text-neutral-300">«{coupon.notes}»</span>}
            />
          ) : null}
        </Card>
      </section>

      <section className="mt-6">
        <h2 className="mb-3 text-sm font-medium text-neutral-300">
          Redemption history{' '}
          {redemptions ? (
            <span className="text-xs text-neutral-500">({redemptions.length})</span>
          ) : null}
        </h2>
        <RedemptionsTable redemptions={redemptions} />
      </section>
    </AdminLayout>
  );
}

// ── Action bar ──────────────────────────────────────────────────────────

function CouponActions({ coupon }: { coupon: AdminCoupon }) {
  const patchMut = usePatchCoupon();
  const deleteMut = useSoftDeleteCoupon();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);

  return (
    <div className="flex flex-wrap gap-2">
      {coupon.isActive ? (
        <Button onClick={() => setPauseOpen(true)} icon={<Pause size={14} />}>
          Pause
        </Button>
      ) : (
        <Button
          onClick={async () => {
            const reason = window.prompt(
              'Resume reason (≥10 chars; recorded in audit log):',
              '',
            );
            if (!reason || reason.trim().length < 10) {
              if (reason !== null) toast.error('Reason must be ≥10 chars');
              return;
            }
            try {
              await patchMut.mutateAsync({
                id: coupon.id,
                patch: { isActive: true, reason: reason.trim() },
              });
              toast.success('Coupon resumed');
            } catch {
              /* toasted */
            }
          }}
          icon={<Play size={14} />}
          tone="ok"
        >
          Resume
        </Button>
      )}
      <Button onClick={() => setEditOpen(true)} icon={<Save size={14} />}>
        Edit limits
      </Button>
      {coupon.isActive ? (
        <Button onClick={() => setDeleteOpen(true)} tone="danger" icon={<Trash2 size={14} />}>
          Disable
        </Button>
      ) : null}

      {pauseOpen ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setPauseOpen(false)}
          title={`Pause ${coupon.code}?`}
          description="Sets is_active=false.  Existing redemptions preserved; future ones refused.  Reversible by Resume."
          confirmLabel="Pause"
          tone="danger"
          pending={patchMut.isPending}
          onConfirm={async (reason) => {
            await patchMut.mutateAsync({
              id: coupon.id,
              patch: { isActive: false, reason },
            });
            toast.success('Coupon paused');
            setPauseOpen(false);
          }}
        />
      ) : null}

      {deleteOpen ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setDeleteOpen(false)}
          title={`Disable ${coupon.code}?`}
          description="Soft delete — code becomes unusable but redemption history preserved.  Operationally equivalent to Pause + lock-out; use this for codes you don't expect to re-enable."
          confirmLabel="Disable"
          tone="danger"
          pending={deleteMut.isPending}
          onConfirm={async (reason) => {
            await deleteMut.mutateAsync({ id: coupon.id, reason });
            toast.success('Coupon disabled');
            setDeleteOpen(false);
          }}
        />
      ) : null}

      {editOpen ? (
        <EditLimitsDialog coupon={coupon} onClose={() => setEditOpen(false)} />
      ) : null}
    </div>
  );
}

function EditLimitsDialog({
  coupon,
  onClose,
}: {
  coupon: AdminCoupon;
  onClose: () => void;
}) {
  const patchMut = usePatchCoupon();
  const [maxRedemptions, setMaxRedemptions] = useState(
    coupon.maxRedemptions === null ? '' : String(coupon.maxRedemptions),
  );
  const [validUntil, setValidUntil] = useState(
    coupon.validUntil ? coupon.validUntil.slice(0, 10) : '',
  );
  const [notes, setNotes] = useState(coupon.notes ?? '');
  const [reason, setReason] = useState('');

  useEffect(() => {
    // Reset on coupon swap
    setMaxRedemptions(coupon.maxRedemptions === null ? '' : String(coupon.maxRedemptions));
    setValidUntil(coupon.validUntil ? coupon.validUntil.slice(0, 10) : '');
    setNotes(coupon.notes ?? '');
    setReason('');
  }, [coupon.id, coupon.maxRedemptions, coupon.validUntil, coupon.notes]);

  const reasonOk = reason.trim().length >= 10;
  const maxRedemptionsParsed =
    maxRedemptions === ''
      ? null
      : Number.isFinite(Number(maxRedemptions)) && Number(maxRedemptions) > 0
        ? Number(maxRedemptions)
        : NaN;
  const maxOk = !Number.isNaN(maxRedemptionsParsed);
  const canSubmit = reasonOk && maxOk && !patchMut.isPending;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <h2 className="mb-1 text-base font-semibold text-neutral-100">Edit limits</h2>
        <p className="mb-4 text-sm text-neutral-400">
          Tune the redemption cap, expiry, and notes without destroying history.
        </p>

        <Field label="Max redemptions (empty = unlimited)">
          <input
            type="number"
            min={1}
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <Field label="Valid until (empty = no expiry)">
          <input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <Field label="Notes (admin only)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <Field label="Reason (≥10 chars; recorded in audit log)">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </Field>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-neutral-800 pt-4">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (!canSubmit) return;
              try {
                await patchMut.mutateAsync({
                  id: coupon.id,
                  patch: {
                    maxRedemptions: maxRedemptionsParsed,
                    validUntil: validUntil
                      ? new Date(validUntil + 'T23:59:59Z').toISOString()
                      : null,
                    // Omit notes when empty — the field is required-when-present
                    // under exactOptionalPropertyTypes.
                    ...(notes.trim() ? { notes: notes.trim() } : {}),
                    reason: reason.trim(),
                  },
                });
                toast.success('Coupon updated');
                onClose();
              } catch {
                /* toasted */
              }
            }}
            disabled={!canSubmit}
            className="rounded-md bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            {patchMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Redemptions table ──────────────────────────────────────────────────

function RedemptionsTable({ redemptions }: { redemptions: CouponRedemption[] | undefined }) {
  if (!redemptions) {
    return <div className="text-sm text-neutral-500">Loading…</div>;
  }
  if (redemptions.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-500">
        No redemptions yet.  Once checkout wires (Phase 1.5 Stripe), customer uses will land here.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="px-4 py-2 font-medium">Customer</th>
            <th className="px-4 py-2 font-medium">Subscription</th>
            <th className="px-4 py-2 font-medium text-right">Discount applied</th>
            <th className="px-4 py-2 font-medium">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          {redemptions.map((r) => {
            const customerHref =
              r.customerType === 'user' ? `/users/${r.customerId}` : `/teams/${r.customerId}`;
            return (
              <tr key={r.id} className="hover:bg-neutral-800/40">
                <td className="px-4 py-2">
                  <Link
                    to={customerHref}
                    className="inline-flex items-center gap-1 text-neutral-200 hover:text-orange-400"
                  >
                    <span className="text-xs uppercase text-neutral-500">{r.customerType}</span>
                    <code className="text-xs">{r.customerId.slice(0, 8)}…</code>
                    <ExternalLink size={11} />
                  </Link>
                </td>
                <td className="px-4 py-2">
                  {r.subscriptionId ? (
                    <Link
                      to="/subscriptions/$id"
                      params={{ id: r.subscriptionId }}
                      className="inline-flex items-center gap-1 text-neutral-300 hover:text-orange-400"
                    >
                      <code className="text-xs">{r.subscriptionId.slice(0, 8)}…</code>
                      <ExternalLink size={11} />
                    </Link>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono text-neutral-200">
                  ${(r.amountOffCents / 100).toFixed(2)}
                </td>
                <td className="px-4 py-2 text-neutral-400">{formatDateTime(r.redeemedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── primitives ─────────────────────────────────────────────────────────

function Button(props: {
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'default' | 'danger' | 'ok';
  icon?: React.ReactNode;
}) {
  const toneClasses = {
    default: 'bg-neutral-800 text-neutral-200 hover:bg-neutral-700',
    danger: 'bg-red-700 text-white hover:bg-red-800',
    ok: 'bg-emerald-700 text-white hover:bg-emerald-800',
  }[props.tone ?? 'default'];
  return (
    <button
      onClick={props.onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-neutral-800 transition-colors',
        toneClasses,
      )}
    >
      {props.icon}
      {props.children}
    </button>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <h2 className="mb-3 text-sm font-medium text-neutral-300">{title}</h2>
      <dl className="space-y-2">{children}</dl>
    </section>
  );
}

function DataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-right text-neutral-200">{value}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
        isActive
          ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
          : 'bg-neutral-700/30 text-neutral-500 ring-neutral-700',
      )}
    >
      {isActive ? 'active' : 'disabled'}
    </span>
  );
}

function humanKind(c: AdminCoupon): string {
  if (c.kind === 'percent') return 'Percent-off coupon';
  if (c.kind === 'fixed') return 'Fixed-amount coupon';
  return 'Plan-grant coupon';
}

function formatDiscount(c: AdminCoupon): string {
  if (c.kind === 'percent') return `${c.percentOff}% off`;
  if (c.kind === 'fixed') return `$${((c.fixedOffCents ?? 0) / 100).toFixed(2)} off`;
  return `${c.planGrantCode} for ${c.planGrantDurationDays}d`;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
